/**
 * pcard-quick-add.js
 *
 * Quick-add behavior for product cards on the collection grid.
 *
 * The collection grid renders card-product.liquid with `suppress_drawer: true`,
 * which sets `data-suppress-drawer="true"` on the form. This script intercepts
 * the submit BEFORE Foxecom's <product-form> custom element gets it, performs
 * the AJAX add-to-cart directly via /cart/add.js, updates the cart count in
 * the header, animates the button through loading -> success states, and
 * never opens the cart drawer.
 *
 * Forms WITHOUT data-suppress-drawer continue to be handled by <product-form>
 * and behave normally (drawer pops open). So FBT, search, related products,
 * etc. retain the existing theme behavior.
 */
(function () {
  'use strict';

  // Use the capturing phase so our handler runs BEFORE <product-form>'s
  // submit listener (which is on the bubble phase). stopImmediatePropagation
  // then prevents <product-form> from running at all, so no drawer.
  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.dataset.suppressDrawer !== 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      handleQuickAdd(form);
    },
    true // capture
  );

  function handleQuickAdd(form) {
    const button = form.querySelector('button[name="add"]');
    if (!button || button.disabled) return;

    setButtonState(button, 'loading');

    const formData = new FormData(form);

    fetch(window.Shopify?.routes?.root
            ? window.Shopify.routes.root + 'cart/add.js'
            : '/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw new Error(err.description || res.statusText || 'Add to cart failed');
          });
        }
        return res.json();
      })
      .then(function () {
        setButtonState(button, 'success');
        refreshCartCount();
        refreshCartDrawer();
        // Auto-reset back to default after a moment so the card is reusable
        setTimeout(function () {
          setButtonState(button, 'default');
        }, 1500);
      })
      .catch(function (err) {
        console.error('[QuickAdd]', err);
        setButtonState(button, 'error');
        setTimeout(function () {
          setButtonState(button, 'default');
        }, 2000);
      });
  }

  /**
   * Refresh the cart drawer's rendered content so the next time the user
   * opens it they see the updated product list.
   *
   * Uses the same approach as Foxecom's existing window.refreshAndOpenCart:
   * read section_id from the <cart-drawer> element's data-section-id
   * attribute (set at render time by cart-drawer.liquid), fetch the section
   * via /?section_id=, parse out the inner #CartDrawer-{id} content, and
   * swap the live drawer's innerHTML.
   *
   * Drawer stays closed throughout — we just update its content.
   */
  function refreshCartDrawer() {
    const cartDrawerEl = document.querySelector('cart-drawer');
    if (!cartDrawerEl) return;

    const sectionId = cartDrawerEl.dataset.sectionId;
    if (!sectionId) {
      console.warn('[QuickAdd] cart-drawer has no data-section-id; skipping drawer refresh');
      return;
    }

    const liveContent = document.getElementById('CartDrawer-' + sectionId);
    if (!liveContent) {
      console.warn('[QuickAdd] could not find #CartDrawer-' + sectionId + ' on page; skipping drawer refresh');
      return;
    }

    fetch('/?section_id=' + encodeURIComponent(sectionId))
      .then(function (res) { return res.text(); })
      .then(function (html) {
        const newContent = new DOMParser()
          .parseFromString(html, 'text/html')
          .getElementById('CartDrawer-' + sectionId);

        if (newContent) {
          liveContent.innerHTML = newContent.innerHTML;
        }
      })
      .catch(function (err) {
        console.warn('[QuickAdd] cart drawer refresh failed:', err);
      });
  }

  /**
   * Update the visible cart count in the header by re-fetching /cart.js
   * and writing the item_count into any [data-cart-count] elements.
   * Also makes [hidden] cart-count badges visible if the cart was previously
   * empty.
   */
  function refreshCartCount() {
    fetch(window.Shopify?.routes?.root
            ? window.Shopify.routes.root + 'cart.js'
            : '/cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        const count = cart.item_count || 0;
        const els = document.querySelectorAll(
          '.cart-count, [data-cart-count]'
        );
        els.forEach(function (el) {
          // Update text content. Some themes wrap the number in a child span;
          // try the inner span first, fall back to the element itself.
          const target = el.querySelector('span') || el;
          target.textContent = String(count);
          if (count > 0) el.removeAttribute('hidden');
        });
        // Foxecom-specific: a body class signals "cart has items"
        if (count > 0) {
          document.body.classList.add('cart-has-items');
        } else {
          document.body.classList.remove('cart-has-items');
        }
        // Dispatch a custom event in case other scripts care
        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { cart: cart } })
        );
      })
      .catch(function (err) {
        console.warn('[QuickAdd] cart count refresh failed:', err);
      });
  }

  /**
   * Apply visual state to the quick-add button.
   * States: 'default', 'loading', 'success', 'error'
   * The actual icons (default plus, spinner, check, x) are toggled via
   * data-state on the button; CSS in card-product-overrides.css selects
   * which icon to show.
   *
   * The tooltip is a sibling of the button (inside the .product-card__quick-add-wrap
   * parent) rather than a descendant, so the disabled-button opacity from
   * theme.css doesn't dim the tooltip text.
   */
  function setButtonState(button, state) {
    button.dataset.state = state;
    const wrap = button.closest('.product-card__quick-add-wrap');
    const tooltip = wrap ? wrap.querySelector('.tooltip') : null;

    // Diagnostic logging — remove once tooltip-update bug is resolved.
    console.log('[QuickAdd] setButtonState', {
      state: state,
      buttonFound: !!button,
      wrapFound: !!wrap,
      tooltipFound: !!tooltip,
      tooltipCurrentText: tooltip ? tooltip.textContent : 'N/A',
      tooltipParent: tooltip ? tooltip.parentElement?.className : 'N/A',
      allTooltipsInWrap: wrap ? wrap.querySelectorAll('.tooltip').length : 0,
    });

    switch (state) {
      case 'loading':
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (tooltip) tooltip.textContent = 'Adding to cart';
        break;
      case 'success':
        button.disabled = true;
        button.removeAttribute('aria-busy');
        if (tooltip) tooltip.textContent = 'Added to cart';
        break;
      case 'error':
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (tooltip) tooltip.textContent = 'Try again';
        break;
      case 'default':
      default:
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (tooltip) tooltip.textContent = 'Add to cart';
        break;
    }
  }
})();
